import Phaser from 'phaser'

import TextureKeys from '../consts/TextureKeys'
import AnimationKeys from '../consts/AnimationKeys'

export default class Drone extends Phaser.GameObjects.Container {
  private droneSprite: Phaser.GameObjects.Sprite
  private initialY: number
  private initialX: number
  private floatTime: number = 0
  private movementType: 'float' | 'vertical' | 'horizontal' | 'circle'
  private movementSpeed: number

  constructor(scene: Phaser.Scene, x: number, y: number, movementType: 'float' | 'vertical' | 'horizontal' | 'circle' = 'float') {
    super(scene, x, y)

    this.initialY = y
    this.initialX = x
    this.movementType = movementType
    this.movementSpeed = Phaser.Math.Between(80, 120)

    // Create the drone sprite WITHOUT adding to scene (use make instead of add)
    this.droneSprite = scene.make.sprite({
      x: 0,
      y: 0,
      key: TextureKeys.DroneSheet,
      frame: 'drone_frame_0.png',
      add: false,
    })
    this.droneSprite.setScale(0.3) // Scale down the drone to fit the game

    // Play animation if it exists
    if (scene.anims.exists(AnimationKeys.DroneHover)) {
      this.droneSprite.play(AnimationKeys.DroneHover)
    }

    this.add(this.droneSprite)

    // Add container to scene
    scene.add.existing(this)

    // Add physics - let group handle this, but we need the body setup
    scene.physics.add.existing(this, true) // Static body

    const body = this.body as Phaser.Physics.Arcade.StaticBody
    // Set collision body to roughly match the drone's size (scaled)
    const width = this.droneSprite.displayWidth * 0.8
    const height = this.droneSprite.displayHeight * 0.8
    body.setSize(width, height)
    body.setOffset(-width * 0.5, -height * 0.5)

    // Reposition body
    body.position.x = this.x + body.offset.x
    body.position.y = this.y + body.offset.y
  }

  // Add preUpdate to ensure sprite animations update and add movement
  preUpdate(time: number, delta: number) {
    // Don't update if drone is inactive (destroyed)
    if (!this.active) return

    // Container needs to update its children
    if (this.droneSprite && this.droneSprite.anims) {
      this.droneSprite.anims.update(0, delta)
    }

    // Only move if we have a body
    if (!this.body) return

    const body = this.body as Phaser.Physics.Arcade.StaticBody

    // Don't re-enable if the body was intentionally disabled (destroyed)
    if (!body.enable) {
      return
    }

    this.floatTime += delta / 1000 // Convert to seconds

    switch (this.movementType) {
      case 'vertical':
        // BIG vertical movement - patrol up and down (slowed by 400%)
        const verticalRange = 300
        const verticalOffset = Math.sin(this.floatTime * (this.movementSpeed / 120)) * verticalRange
        this.y = this.initialY + verticalOffset
        break

      case 'horizontal':
        // BIG horizontal movement - patrol left and right (slowed by 400%)
        const horizontalRange = 200
        const horizontalOffset = Math.sin(this.floatTime * (this.movementSpeed / 120)) * horizontalRange
        this.x = this.initialX + horizontalOffset
        break

      case 'circle':
        // Circular movement (slowed by 400%)
        const circleRadius = 100
        const angle = this.floatTime * (this.movementSpeed / 200)
        this.x = this.initialX + Math.cos(angle) * circleRadius
        this.y = this.initialY + Math.sin(angle) * circleRadius
        break

      case 'float':
      default:
        // Gentle floating (slowed by 400%)
        const floatOffset = Math.sin(this.floatTime * 0.5) * 15
        this.y = this.initialY + floatOffset
        break
    }

    // CRITICAL: Always sync physics body position with container position
    // This ensures collision detection works even during movement
    if (body && body.position) {
      body.position.x = this.x + body.offset.x
      body.position.y = this.y + body.offset.y

      // Force world to recognize the body update
      body.updateCenter()
    }
  }

  // Update initial position when wrapping
  updateInitialPosition(newX: number, newY: number) {
    this.initialX = newX
    this.initialY = newY
  }

  // Get the movement type for cleanup logic
  getMovementType(): 'float' | 'vertical' | 'horizontal' | 'circle' {
    return this.movementType
  }
}
